import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { AlertTriangle, Check, Copy } from 'lucide-react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { type ProcessorDetails, useProcessorById, useUpdateProcessor } from '@/lib/api/processors'
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id'
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SecretInput } from '@/components/form/secret-input'
import { useToast } from '@/components/ui/use-toast'
import { ProcessorEditHeader } from '../../components/processor-edit-header'
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field'
import { unlimitWebhookUrl } from '../webhook-url'

const STORED_CARD_MODES = ['unlimit_vault', 'merchant_vault'] as const
type StoredCardMode = (typeof STORED_CARD_MODES)[number]

interface UnlimitFormState {
  name: string
  enabled: boolean
  terminalCode: string
  password: string
  webhookSecret: string
  storedCardMode: StoredCardMode
  testMode: boolean
  dryMode: boolean
}

const readString = (options: Record<string, unknown> | null | undefined, key: string): string => {
  const value = options?.[key]
  return typeof value === 'string' ? value : ''
}

const toStoredCardMode = (value: unknown): StoredCardMode =>
  STORED_CARD_MODES.find((mode) => mode === value) ?? 'unlimit_vault'

const formStateFromProcessor = (processor: ProcessorDetails): UnlimitFormState => {
  const options = processor.options
  return {
    name: processor.name,
    enabled: processor.enabled ?? true,
    terminalCode: readString(options, 'terminalCode'),
    password: readString(options, 'password'),
    webhookSecret: readString(options, 'webhookSecret'),
    storedCardMode: toStoredCardMode(options?.storedCardMode),
    testMode: options?.testMode === true,
    dryMode: options?.dryMode === true,
  }
}

const UnlimitEditPageInner = () => {
  const id = useProcessorEditId()
  const { t } = useTranslation('processors')
  const { data: processor, isLoading } = useProcessorById(id)

  if (isLoading) {
    return <div className="container mx-auto py-8" />
  }

  if (!processor || processor.type !== 'unlimit') {
    return (
      <div className="container mx-auto py-8">
        <Alert variant="destructive">
          <AlertDescription>{t('processorNotFound')}</AlertDescription>
        </Alert>
      </div>
    )
  }

  return <UnlimitEditForm processor={processor} />
}

type Props = {
  processor: ProcessorDetails
}

const UnlimitEditForm = ({ processor }: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslation('processors')
  const { toast } = useToast()
  const processorExit = useProcessorEditExit()
  const updateProcessorMutation = useUpdateProcessor()

  const [validationError, setValidationError] = useState<string | null>(null)
  const [isWebhookCopied, setIsWebhookCopied] = useState(false)
  const [formData, setFormData] = useState<UnlimitFormState>(() => formStateFromProcessor(processor))

  const webhookUrl = unlimitWebhookUrl(processor.id)
  const isMerchantVault = formData.storedCardMode === 'merchant_vault'

  const validate = (): string | null => {
    if (!formData.name.trim()) return t('nameRequired')
    if (!formData.terminalCode.trim()) return t('unlimit.validation.terminalCodeRequired')
    if (!formData.password.trim()) return t('unlimit.validation.passwordRequired')
    if (!formData.webhookSecret.trim()) return t('unlimit.validation.callbackSecretRequired')
    return null
  }

  const handleSubmit = async () => {
    const error = validate()
    setValidationError(error)
    if (error) return

    try {
      await updateProcessorMutation.mutateAsync({
        processorId: processor.id,
        updatedData: {
          type: processor.type,
          name: formData.name,
          enabled: formData.enabled,
          options: {
            terminalCode: formData.terminalCode.trim(),
            password: formData.password.trim(),
            webhookSecret: formData.webhookSecret.trim(),
            storedCardMode: formData.storedCardMode,
            testMode: formData.testMode,
            dryMode: formData.dryMode,
          },
        },
      })

      toast({ description: t('processorUpdated') })
      if (processorExit.isEmbed) {
        processorExit.exitAfterSave()
        return
      }
      navigate(-1)
    } catch {
      toast({ description: t('failedToSave'), variant: 'destructive' })
    }
  }

  const handleCancel = () => {
    if (processorExit.isEmbed) {
      processorExit.exitCancel()
      return
    }
    navigate(-1)
  }

  const handleCopyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl)
      setIsWebhookCopied(true)
      setTimeout(() => setIsWebhookCopied(false), 2000)
      toast({ description: t('webhookUrlCopied') })
    } catch {
      toast({ description: t('failedToCopyWebhookUrl'), variant: 'destructive' })
    }
  }

  return (
    <div className="container mx-auto py-8">
      <ProcessorEditHeader
        processorId={processor.id}
        title={t('unlimit.editTitle')}
        onCancel={handleCancel}
        onSave={handleSubmit}
        isSaving={updateProcessorMutation.isPending}
      />

      {validationError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('generalSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t('processorName')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked === true })}
              />
              <Label htmlFor="enabled">{t('enabled')}</Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('apiCredentials')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-sm text-blue-700">
                <strong>{t('unlimit.credentials.howToGetTitle')}</strong>
              </p>
              <ol className="mt-2 list-decimal pl-4 text-sm text-blue-700">
                <li>{t('unlimit.credentials.step1')}</li>
                <li>
                  <Trans i18nKey="unlimit.credentials.step2" ns="processors" components={{ b: <b /> }} />
                </li>
                <li>{t('unlimit.credentials.step3')}</li>
              </ol>
            </div>

            <div>
              <Label htmlFor="terminalCode">{t('unlimit.credentials.terminalCodeLabel')} *</Label>
              <Input
                id="terminalCode"
                value={formData.terminalCode}
                onChange={(e) => setFormData({ ...formData, terminalCode: e.target.value })}
                placeholder="654321"
                className="font-mono"
              />
              <p className="text-muted-foreground mt-1 text-xs">{t('unlimit.credentials.terminalCodeDescription')}</p>
            </div>

            <SecretInput
              label={t('unlimit.credentials.passwordLabel') + ' *'}
              value={formData.password}
              onChange={(value) => setFormData({ ...formData, password: value })}
              placeholder="••••••••"
            />
            <SecretInput
              label={t('unlimit.credentials.callbackSecretLabel') + ' *'}
              value={formData.webhookSecret}
              onChange={(value) => setFormData({ ...formData, webhookSecret: value })}
              placeholder="••••••••"
            />

            <div className="space-y-2">
              <Label>{t('webhookUrl')}</Label>
              <div className="flex items-center gap-2">
                <Input value={webhookUrl} readOnly className="font-mono text-xs" aria-label={t('webhookUrl')} />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCopyWebhook}
                  aria-label={t('unlimit.webhook.copy')}
                >
                  {isWebhookCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{t('unlimit.webhook.description')}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('unlimit.storedCardMode.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Label htmlFor="storedCardMode">{t('unlimit.storedCardMode.label')}</Label>
            <Select
              value={formData.storedCardMode}
              onValueChange={(value) => setFormData({ ...formData, storedCardMode: toStoredCardMode(value) })}
            >
              <SelectTrigger id="storedCardMode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unlimit_vault">{t('unlimit.storedCardMode.unlimitVault')}</SelectItem>
                <SelectItem value="merchant_vault">{t('unlimit.storedCardMode.merchantVault')}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              {isMerchantVault
                ? t('unlimit.storedCardMode.merchantVaultDescription')
                : t('unlimit.storedCardMode.unlimitVaultDescription')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('testModeSettings')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="testMode"
                checked={formData.testMode}
                onCheckedChange={(checked) => setFormData({ ...formData, testMode: checked === true })}
              />
              <Label htmlFor="testMode">{t('enableTestMode')}</Label>
            </div>
            <p className="text-muted-foreground mt-1 text-xs">{t('unlimit.testModeDescription')}</p>
          </CardContent>
        </Card>

        <ProcessorDryModeField
          checked={formData.dryMode}
          onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
          disabled={updateProcessorMutation.isPending}
        />
      </div>
    </div>
  )
}

export const UnlimitEditPage = withRequiredPermission(UnlimitEditPageInner, PERMISSIONS.PROCESSORS_MANAGE)
